import { useState, useEffect } from "react";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api } from "@/lib/api";
import type { RecruiterDashboardData, CandidateRow } from "@/lib/types";

function formatActivity(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  ink: "#17131F",
  paper: "#FBFAFE",
  accent: "var(--accent)",
  accentLight: "var(--accent-50)",
  slate: "#7C7590",
  good: "#1A7A4A",
  warn: "#B5811F",
  white: "#FFFFFF",
  border: "#E8E4DC",
  navy: "#1A3552",
} as const;

const cardStyle: React.CSSProperties = {
  background: T.white,
  borderRadius: 18,
  padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: T.accent,
};

// ── Avatar ────────────────────────────────────────────────────────────────────
function Avatar({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: T.navy,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          color: T.white,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.04em",
        }}
      >
        {initials}
      </span>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: CandidateRow["status"] }) {
  const config: Record<
    CandidateRow["status"],
    { dot: string; text: string; border: string; bg: string; label: string }
  > = {
    invited: {
      dot: T.accent,
      text: T.accent,
      border: "rgba(122,107,224,0.35)",
      bg: "rgba(122,107,224,0.07)",
      label: "Invited",
    },
    in_progress: {
      dot: T.warn,
      text: T.warn,
      border: "rgba(180,83,9,0.35)",
      bg: "rgba(180,83,9,0.07)",
      label: "In progress",
    },
    completed: {
      dot: T.good,
      text: T.good,
      border: "rgba(26,122,74,0.35)",
      bg: "rgba(26,122,74,0.07)",
      label: "Completed",
    },
    expired: {
      dot: T.slate,
      text: T.slate,
      border: "rgba(107,122,140,0.35)",
      bg: "rgba(107,122,140,0.07)",
      label: "Expired",
    },
  };
  const c = config[status] ?? config.expired;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 999,
        border: `1px solid ${c.border}`,
        background: c.bg,
        fontSize: 11.5,
        fontWeight: 600,
        color: c.text,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: c.dot,
          flexShrink: 0,
        }}
      />
      {c.label}
    </span>
  );
}

// ── Donut chart ───────────────────────────────────────────────────────────────
function DonutChart({
  segs,
  total,
}: {
  segs: RecruiterDashboardData["donut_segs"];
  total: number;
}) {
  const R = 54;
  const CX = 70;
  const CY = 70;
  const circ = 2 * Math.PI * R;

  // Build segments from scratch using counts
  let acc = 0;
  const segments = segs.map((s) => {
    const pct = total > 0 ? s.count / total : 0;
    const dash = pct * circ;
    const gap = circ - dash;
    const rotate = (acc / (total || 1)) * 360 - 90;
    acc += s.count;
    return { ...s, dash, gap, rotate };
  });

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}
    >
      <svg width={140} height={140} viewBox="0 0 140 140">
        {total === 0 ? (
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            stroke="#E8E4DC"
            strokeWidth={14}
          />
        ) : (
          segments.map((s, i) =>
            s.count > 0 ? (
              <circle
                key={i}
                cx={CX}
                cy={CY}
                r={R}
                fill="none"
                stroke={s.color}
                strokeWidth={14}
                strokeDasharray={`${s.dash} ${s.gap}`}
                strokeLinecap="butt"
                transform={`rotate(${s.rotate} ${CX} ${CY})`}
              />
            ) : null
          )
        )}
        {/* Centre text */}
        <text
          x={CX}
          y={CY - 6}
          textAnchor="middle"
          fontSize={24}
          fontWeight={700}
          fill={T.ink}
          fontFamily="inherit"
        >
          {total}
        </text>
        <text
          x={CX}
          y={CY + 12}
          textAnchor="middle"
          fontSize={9}
          fontWeight={700}
          fill={T.slate}
          style={{ fontFamily: "var(--font-mono)" }}
          letterSpacing="0.1em"
        >
          TOTAL
        </text>
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
        {segs.map((s, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: s.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 12.5, color: T.slate }}>{s.label}</span>
            </div>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
              {s.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Onboarding coachmarks card ────────────────────────────────────────────────
function CoachmarksCard({ onDismiss }: { onDismiss: () => void }) {
  const [dismissing, setDismissing] = useState(false);

  const steps = [
    {
      n: "01",
      title: "Send an invite",
      desc: "Enter a candidate's name, role and email to create a personalised coaching session.",
    },
    {
      n: "02",
      title: "Candidate prepares",
      desc: "They receive a magic-link, upload their CV and complete a live AI practice interview.",
    },
    {
      n: "03",
      title: "Review the scorecard",
      desc: "A structured scorecard lands in your dashboard the moment the session ends.",
    },
  ];

  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await api.post("/recruiter/onboarding/dismiss");
    } catch {
      // Dismiss locally even if the request fails
    }
    onDismiss();
  };

  return (
    <div
      style={{
        ...cardStyle,
        position: "relative",
        marginBottom: 24,
      }}
    >
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        disabled={dismissing}
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          background: "rgba(107,122,140,0.1)",
          border: "none",
          borderRadius: 999,
          padding: "4px 12px",
          fontSize: 12,
          fontWeight: 500,
          color: T.slate,
          cursor: dismissing ? "default" : "pointer",
          opacity: dismissing ? 0.6 : 1,
          transition: "opacity 0.12s",
        }}
      >
        Ok, got it
      </button>

      {/* Label */}
      <p style={{ ...monoLabel, marginBottom: 20 }}>GET STARTED IN 3 STEPS</p>

      {/* 3-column steps */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 24,
          marginBottom: 24,
        }}
      >
        {steps.map((s) => (
          <div key={s.n}>
            <span
              style={{
                ...monoLabel,
                fontSize: 11,
                display: "block",
                marginBottom: 6,
              }}
            >
              {s.n}
            </span>
            <p
              style={{
                margin: "0 0 6px",
                fontSize: 14,
                fontWeight: 700,
                color: T.ink,
              }}
            >
              {s.title}
            </p>
            <p style={{ margin: 0, fontSize: 13, color: T.slate, lineHeight: 1.5 }}>
              {s.desc}
            </p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <a
        href="/coach/recruiter/invite/new"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: T.accent,
          color: T.white,
          borderRadius: 999,
          padding: "8px 20px",
          fontSize: 13.5,
          fontWeight: 600,
          textDecoration: "none",
          transition: "opacity 0.12s",
        }}
      >
        Send your first invite →
      </a>
    </div>
  );
}

// ── Checklist card ────────────────────────────────────────────────────────────
function ChecklistCard({
  steps,
}: {
  steps: Array<{ label: string; done: boolean }>;
}) {
  return (
    <div
      style={{
        ...cardStyle,
        borderLeft: `3px solid ${T.accent}`,
        marginBottom: 24,
      }}
    >
      <p style={{ ...monoLabel, marginBottom: 16 }}>
        GETTING STARTED — YOUR FIRST WEEK
      </p>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px 24px",
        }}
      >
        {steps.map((step, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            {step.done ? (
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: T.good,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 12,
                  color: T.white,
                  fontWeight: 700,
                }}
              >
                ✓
              </span>
            ) : (
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: "2px solid #CBD5E0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 11,
                  color: T.slate,
                  fontWeight: 700,
                }}
              >
                {i + 1}
              </span>
            )}
            <span
              style={{
                fontSize: 13,
                fontWeight: step.done ? 700 : 400,
                color: step.done ? T.ink : T.slate,
                whiteSpace: "nowrap",
              }}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Live candidates table ─────────────────────────────────────────────────────
function LiveCandidatesCard({ rows }: { rows: CandidateRow[] }) {
  const thStyle: React.CSSProperties = {
    padding: "0 8px 10px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    color: T.slate,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${T.border}`,
    whiteSpace: "nowrap",
  };

  const tdStyle: React.CSSProperties = {
    padding: "12px 8px",
    verticalAlign: "middle",
  };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2
          style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.ink }}
        >
          Live candidates
        </h2>
        <a
          href="/coach/recruiter/candidates"
          style={{ fontSize: 13, color: T.accent, textDecoration: "none", fontWeight: 500 }}
        >
          View all →
        </a>
      </div>

      {rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13.5, color: T.slate }}>
          No active candidates yet.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13.5,
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Candidate</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.id}
                  style={{
                    borderBottom:
                      i < rows.length - 1 ? `1px solid ${T.border}` : "none",
                  }}
                >
                  <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar name={row.name} />
                      <div>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: 600,
                            color: T.ink,
                            fontSize: 13.5,
                          }}
                        >
                          {row.name}
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 12,
                            color: T.slate,
                          }}
                        >
                          {row.role}
                          {row.company ? ` · ${row.company}` : ""}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <StatusBadge status={row.status} />
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontSize: 12.5,
                      color: T.slate,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatActivity(row.last_activity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer link */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <a
          href="/coach/recruiter/candidates"
          style={{ fontSize: 13, color: T.accent, textDecoration: "none", fontWeight: 500 }}
        >
          Manage all candidates →
        </a>
      </div>
    </div>
  );
}

// ── Recently completed card ───────────────────────────────────────────────────
function RecentlyCompletedCard({
  items,
}: {
  items: RecruiterDashboardData["completed_recent"];
}) {
  return (
    <div style={cardStyle}>
      <h2
        style={{
          margin: "0 0 16px",
          fontSize: 15,
          fontWeight: 700,
          color: T.ink,
        }}
      >
        Recently completed
      </h2>

      {items.length === 0 ? (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <p
            style={{
              margin: "0 0 4px",
              fontSize: 13.5,
              fontWeight: 600,
              color: T.slate,
            }}
          >
            No completed sessions yet
          </p>
          <p style={{ margin: 0, fontSize: 12.5, color: T.slate }}>
            Scores and scorecards will appear here.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {items.map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                paddingBottom: i < items.length - 1 ? 12 : 0,
                borderBottom:
                  i < items.length - 1 ? `1px solid ${T.border}` : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <Avatar name={item.name} />
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      color: T.ink,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.name}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 11.5,
                      color: T.slate,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.role}
                    {item.company ? ` · ${item.company}` : ""}
                  </p>
                </div>
              </div>
              {item.score && (
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: item.score_color || T.good,
                    flexShrink: 0,
                  }}
                >
                  {item.score}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function RecruiterDashboard() {
  const [data, setData] = useState<RecruiterDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coachmarksDismissed, setCoachmarksDismissed] = useState(false);

  useEffect(() => {
    api
      .get<RecruiterDashboardData>("/recruiter/dashboard")
      .then(setData)
      .catch((err: Error) => setError(err.message ?? "Failed to load dashboard"));
  }, []);

  if (error) {
    return (
      <RecruiterShell activeNav="home">
        <div
          style={{
            padding: 32,
            color: "#B91C1C",
            fontSize: 14,
          }}
        >
          Error: {error}
        </div>
      </RecruiterShell>
    );
  }

  if (!data) {
    return (
      <RecruiterShell activeNav="home">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            fontSize: 14,
            color: T.slate,
          }}
        >
          Loading…
        </div>
      </RecruiterShell>
    );
  }

  const showCoachmarks = !data.ob_all_done && !coachmarksDismissed;
  const totalCandidates =
    data.stats.invited + data.stats.active + data.stats.completed + data.stats.expired;

  const statsCards = [
    {
      label: "AWAITING START",
      value: data.stats.invited,
      subtext: "Invites sent, not yet begun",
      dotColor: T.accent,
      numColor: T.accent,
    },
    {
      label: "ACTIVE SESSIONS",
      value: data.stats.active,
      subtext: "Interviews in progress",
      dotColor: T.warn,
      numColor: T.warn,
    },
    {
      label: "COMPLETED",
      value: data.stats.completed,
      subtext: "This account",
      dotColor: T.accent,
      numColor: T.accent,
    },
  ];

  return (
    <RecruiterShell activeNav="home">
      {/* ── Header row ─────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 28,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 26,
              fontWeight: 700,
              color: T.ink,
              lineHeight: 1.2,
            }}
          >
            {data.greeting_prefix
              ? `${data.greeting_prefix}, ${data.recruiter_name}.`
              : `Good day, ${data.recruiter_name}.`}
          </h1>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13.5,
              color: T.slate,
            }}
          >
            {data.brand_date}
          </p>
        </div>

        <a
          href="/coach/recruiter/invite/new"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: T.accent,
            color: T.white,
            borderRadius: 999,
            padding: "9px 20px",
            fontSize: 13.5,
            fontWeight: 600,
            textDecoration: "none",
            flexShrink: 0,
            transition: "opacity 0.12s",
          }}
        >
          + Send new invite
        </a>
      </div>

      {/* ── Coachmarks ─────────────────────────────────────────────── */}
      {showCoachmarks && (
        <CoachmarksCard onDismiss={() => setCoachmarksDismissed(true)} />
      )}

      {/* ── Stats row ──────────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {statsCards.map((s) => (
          <div key={s.label} style={cardStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: s.dotColor,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  ...monoLabel,
                  fontSize: 9.5,
                }}
              >
                {s.label}
              </span>
            </div>
            <p
              style={{
                margin: "0 0 4px",
                fontSize: 42,
                fontWeight: 700,
                lineHeight: 1,
                color: s.numColor,
              }}
            >
              {s.value}
            </p>
            <p style={{ margin: 0, fontSize: 12.5, color: T.slate }}>
              {s.subtext}
            </p>
          </div>
        ))}
      </div>

      {/* ── Onboarding checklist ────────────────────────────────────── */}
      <ChecklistCard steps={data.ob_checklist} />

      {/* ── Bottom two-column ───────────────────────────────────────── */}
      <div
        className="dashboard-bottom-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
          gap: 16,
          alignItems: "start",
        }}
      >
        {/* Left: Live candidates */}
        <LiveCandidatesCard rows={data.snapshot} />

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Pipeline overview */}
          <div style={cardStyle}>
            <h2
              style={{
                margin: "0 0 20px",
                fontSize: 15,
                fontWeight: 700,
                color: T.ink,
              }}
            >
              Pipeline overview
            </h2>
            <DonutChart segs={data.donut_segs} total={totalCandidates} />
          </div>

          {/* Recently completed */}
          <RecentlyCompletedCard items={data.completed_recent} />
        </div>
      </div>

      {/* Responsive grid collapse for mobile */}
      <style>{`
        @media (max-width: 720px) {
          .dashboard-bottom-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>
    </RecruiterShell>
  );
}
